import { FrontendToolHandler } from '../types'
import { getActiveAgentBus, isAgentBusBound, type Fdc3LiteContext } from '../fdc3Bridge'

/**
 * `runIntent` (Spec 010 §7) — raise an FDC3 intent with a context, optionally at a
 * named app. The riskiest verb: it can both **launch** an app and **deliver** data,
 * so it requires confirmation (the executor's pending-queue + confirm/reject UI runs
 * unchanged). All resolution, launch gating and delivery are the Spec 008 host's;
 * Ali gains no privilege the user lacks.
 *
 * Only offered when Ali is bound to a Desktop Agent; a no-op off the bus.
 */
export const runIntent: FrontendToolHandler = {
  enabled: isAgentBusBound,
  definition: {
    name: 'runIntent',
    description:
      'Raise an FDC3 intent (e.g. ViewChart) with a context, optionally targeting a named app.',
    parameters: {
      type: 'object',
      properties: {
        intent: {
          type: 'string',
          description: 'The intent name from state.workspaceContext.availableIntents.'
        },
        context: {
          type: 'object',
          description: 'An FDC3 context object with a string `type`, e.g. fdc3.instrument.'
        },
        appName: {
          type: 'string',
          description: 'Optional uniqueName of a specific target app from state.availableApps.'
        }
      },
      required: ['intent', 'context']
    },
    requiresConfirmation: true
  },
  execute: async (args) => {
    const bus = getActiveAgentBus()
    if (!bus || !bus.isBound()) {
      return { success: false, message: 'Ali is not connected to a workspace bus.' }
    }
    const intent = typeof args.intent === 'string' ? args.intent.trim() : ''
    if (!intent) {
      return { success: false, message: 'An intent name is required.' }
    }
    const context = args.context as Fdc3LiteContext
    if (!context || typeof context.type !== 'string') {
      return { success: false, message: 'A context with a string `type` is required.' }
    }
    const appName =
      typeof args.appName === 'string' && args.appName.trim() ? args.appName.trim() : undefined
    const result = await bus.runIntent(intent, context, appName)
    return { success: result.ok, message: result.message, data: result.data }
  }
}
